import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowLeft, Pencil, Send, PauseCircle, PlayCircle, MoreHorizontal, Globe, EyeOff,
  Trash2, XCircle, Users, CalendarDays, FileText, BarChart3, Link2, Briefcase, AlertTriangle, Filter,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getPosition, updatePosition, deletePosition, publishPosition, pausePosition,
  getPositionReleasePreview, getCandidates, getInterviewSchedule,
} from '../api';
import {
  Position, User, Candidate, ReleasePreview, STAGE_LABELS, CandidateStage,
  HC_EMPLOYMENT_LABELS, POSITION_STATUS_LABELS,
} from '../types';
import { PositionStatusBadge, PriorityBadge, Avatar, fmtDate, fmtDateTime } from './positionShared';
import PositionBindingTab from './PositionBindingTab';
import PositionScreeningTab from './PositionScreeningTab';
import CreatePositionDrawer from './CreatePositionDrawer';

interface Props {
  positionId: string;
  currentUser: User;
  initialTab?: string;
  notificationBell?: React.ReactNode;
  onBack: () => void;
  /** 岗位数据变化后刷新 App 层的岗位列表 */
  onUpdate: () => void;
  /** 跳到候选人模块并按该岗位过滤 */
  onViewCandidates: (positionId: string) => void;
  /** 打开候选人详情 */
  onOpenCandidate: (candidateId: string) => void;
  /** 跳到编制管理页并定位到该编制编号 */
  onOpenSlot?: (code: string) => void;
}

type DetailTab = 'detail' | 'binding' | 'screening' | 'jd' | 'candidates' | 'interviews' | 'analytics';

const TABS: { key: DetailTab; label: string; icon: React.ElementType }[] = [
  { key: 'detail', label: '岗位详情', icon: Briefcase },
  { key: 'binding', label: '编制绑定', icon: Link2 },
  { key: 'screening', label: '简历筛选', icon: Filter },
  { key: 'jd', label: 'JD 预览', icon: FileText },
  { key: 'candidates', label: '候选人', icon: Users },
  { key: 'interviews', label: '面试安排', icon: CalendarDays },
  { key: 'analytics', label: '数据分析', icon: BarChart3 },
];

/** 招聘进展口径：与候选人阶段一一对应，避免与列表页 funnel 出现两套算法 */
const PROGRESS_GROUPS: { label: string; stages: CandidateStage[]; color: string }[] = [
  { label: '进行中', stages: ['new', 'screening', 'screening_passed', 'first_interview', 'second_interview', 'final_interview', 'offer', 'pending_onboard'], color: 'text-gray-800' },
  { label: '待面试', stages: ['screening_passed', 'first_interview', 'second_interview', 'final_interview'], color: 'text-primary-600' },
  { label: 'Offer 中', stages: ['offer', 'pending_onboard'], color: 'text-gray-600' },
  { label: '已入职', stages: ['onboarded'], color: 'text-primary-600' },
  { label: '已淘汰', stages: ['screening_rejected', 'first_interview_rejected', 'second_interview_rejected', 'final_interview_rejected', 'offer_rejected'], color: 'text-gray-400' },
];

const FUNNEL_STAGES: CandidateStage[] = [
  'new', 'screening', 'screening_passed', 'first_interview', 'second_interview',
  'final_interview', 'offer', 'pending_onboard', 'onboarded',
];

function MetaItem({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className="text-sm text-gray-700 mt-0.5">{value || <span className="text-gray-300">—</span>}</div>
    </div>
  );
}

export default function PositionDetail({
  positionId, currentUser, initialTab, notificationBell,
  onBack, onUpdate, onViewCandidates, onOpenCandidate, onOpenSlot,
}: Props) {
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  const [position, setPosition] = useState<Position | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DetailTab>(
    TABS.some(t => t.key === initialTab) ? (initialTab as DetailTab) : 'detail'
  );
  const [showEdit, setShowEdit] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // 暂停/关闭二次确认：intent 决定确认后走哪个流转
  const [confirmIntent, setConfirmIntent] = useState<'pause' | 'close' | null>(null);
  const [preview, setPreview] = useState<ReleasePreview | null>(null);
  const [acting, setActing] = useState(false);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [interviews, setInterviews] = useState<any[]>([]);

  const reload = useCallback(async () => {
    try {
      const p = await getPosition(positionId);
      setPosition(p);
    } catch {
      toast.error('加载岗位失败');
    } finally {
      setLoading(false);
    }
  }, [positionId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    getCandidates({ position_id: positionId, limit: '500' })
      .then(d => setCandidates(d.candidates || []))
      .catch(() => {});
  }, [positionId]);

  // 面试安排：复用看板的日程接口（近 60 天），按该岗位的候选人过滤，不新增后端接口
  useEffect(() => {
    if (tab !== 'interviews' || candidates.length === 0) return;
    const today = new Date();
    const start = new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
    const end = new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10);
    const ids = new Set(candidates.map(c => c.id));
    getInterviewSchedule(start, end)
      .then(d => setInterviews((d.interviews || []).filter((iv: any) => ids.has(iv.candidate_id))))
      .catch(() => {});
  }, [tab, candidates]);

  const closeMore = useCallback((e: MouseEvent) => {
    if (moreRef.current && !moreRef.current.contains(e.target as Node)) setShowMore(false);
  }, []);
  useEffect(() => {
    document.addEventListener('mousedown', closeMore);
    return () => document.removeEventListener('mousedown', closeMore);
  }, [closeMore]);

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    candidates.forEach(c => { counts[c.stage] = (counts[c.stage] || 0) + 1; });
    return counts;
  }, [candidates]);

  const countStages = (stages: CandidateStage[]) =>
    stages.reduce((sum, s) => sum + (stageCounts[s] || 0), 0);

  const openConfirm = async (intent: 'pause' | 'close') => {
    setConfirmIntent(intent);
    setPreview(null);
    try {
      setPreview(await getPositionReleasePreview(positionId));
    } catch {
      // 预览失败不阻断操作，弹窗上以提示代替明细
      setPreview({ will_release: [], will_keep: [] });
    }
  };

  const runTransition = async () => {
    if (!confirmIntent) return;
    setActing(true);
    try {
      const result = confirmIntent === 'pause'
        ? await pausePosition(positionId)
        : await updatePosition(positionId, { status: 'closed' });
      const released = result?.released?.length || 0;
      toast.success(confirmIntent === 'pause'
        ? `已暂停招聘${released ? `，释放 ${released} 个编制` : ''}`
        : `岗位已关闭${released ? `，释放 ${released} 个编制` : ''}`);
      setConfirmIntent(null);
      await reload();
      onUpdate();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || '操作失败');
    } finally {
      setActing(false);
    }
  };

  const handlePublish = async () => {
    setActing(true);
    try {
      await publishPosition(positionId);
      toast.success('岗位已发布，进入招聘中');
      await reload();
      onUpdate();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || '发布失败');
    } finally {
      setActing(false);
    }
  };

  const handleResume = async () => {
    setActing(true);
    try {
      // paused → open 复用 publish（内部保留首次 published_at）；暂停时编制已释放，需重新绑定
      await publishPosition(positionId);
      toast.success('已恢复招聘，请确认编制绑定');
      await reload();
      onUpdate();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || '操作失败');
    } finally {
      setActing(false);
    }
  };

  const handleTogglePublic = async () => {
    if (!position) return;
    try {
      await updatePosition(positionId, { is_public: position.is_public ? 0 : 1 });
      toast.success(position.is_public ? '已取消公开' : '已公开到招聘门户');
      await reload();
      onUpdate();
    } catch { toast.error('更新失败'); }
  };

  const handleDelete = async () => {
    if (!position) return;
    if (!window.confirm(`确认删除岗位「${position.name}」？归属该岗位的候选人将变为未分配。`)) return;
    try {
      await deletePosition(positionId);
      toast.success('岗位已删除');
      onUpdate();
      onBack();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '删除失败');
    }
  };

  if (loading || !position) {
    return (
      <div className="h-full flex items-center justify-center">
        {loading
          ? <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
          : <div className="text-sm text-gray-400">岗位不存在或已被删除</div>}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-2.5">
      {/* Header */}
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 pt-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400 min-w-0">
          <button onClick={onBack} className="hover:text-primary-600 shrink-0">岗位管理</button>
          <span className="shrink-0">/</span>
          <span className="text-gray-600 truncate">{position.name}</span>
        </div>

        <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 md:flex-nowrap">
              <button onClick={onBack} className="p-1 -ml-1 hover:bg-gray-100 rounded-lg shrink-0">
                <ArrowLeft size={17} className="text-gray-500" />
              </button>
              <h1 className="text-lg font-bold text-gray-800 truncate">{position.name}</h1>
              <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-1.5 whitespace-nowrap md:w-auto md:flex-nowrap md:shrink-0">
                <PositionStatusBadge status={position.status} />
                <PriorityBadge value={position.priority} />
                {position.is_public ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-primary-600 bg-primary-50 border border-primary-200 inline-flex items-center gap-0.5">
                    <Globe size={9} />公开
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:shrink-0">
            {notificationBell}
            {isHr && (
              <>
                <button onClick={() => setShowEdit(true)}
                  className="h-9 px-3 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                  <Pencil size={13} />编辑
                </button>
                {position.status === 'draft' && (
                  <button onClick={handlePublish} disabled={acting}
                    className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                    <Send size={13} />发布
                  </button>
                )}
                {position.status === 'open' && (
                  <button onClick={() => openConfirm('pause')} disabled={acting}
                    className="h-9 px-3 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
                    <PauseCircle size={13} />暂停招聘
                  </button>
                )}
                {position.status === 'paused' && (
                  <button onClick={handleResume} disabled={acting}
                    className="h-9 px-3 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 flex items-center gap-1.5">
                    <PlayCircle size={13} />恢复招聘
                  </button>
                )}
                <div ref={moreRef} className="relative">
                  <button onClick={() => setShowMore(!showMore)}
                    className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">
                    <MoreHorizontal size={16} />
                  </button>
                  {showMore && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg border border-gray-200 shadow-lg z-40 py-1">
                      <button onClick={() => { setShowMore(false); handleTogglePublic(); }}
                        className="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        {position.is_public
                          ? <><EyeOff size={13} className="text-gray-400" />取消公开</>
                          : <><Globe size={13} className="text-gray-400" />公开到门户</>}
                      </button>
                      {position.status !== 'closed' && (
                        <button onClick={() => { setShowMore(false); openConfirm('close'); }}
                          className="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                          <XCircle size={13} className="text-gray-400" />关闭岗位
                        </button>
                      )}
                      <button onClick={() => { setShowMore(false); handleDelete(); }}
                        className="w-full px-3 py-2 text-xs text-left text-gray-500 hover:bg-gray-50 flex items-center gap-2">
                        <Trash2 size={13} />删除岗位
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
          <span>编号 {position.code || '—'}</span>
          <span>部门 {position.department || '未分配'}</span>
          <span>地点 {position.location || '—'}</span>
          <span>类型 {HC_EMPLOYMENT_LABELS[position.employment_type as keyof typeof HC_EMPLOYMENT_LABELS] || '—'}</span>
          <span className="flex items-center gap-1">
            负责人 {position.owner_name ? <><Avatar name={position.owner_name} size={16} />{position.owner_name}</> : '未指派'}
          </span>
          <span>创建 {fmtDate(position.created_at)}</span>
          <span>更新 {fmtDate(position.updated_at)}</span>
        </div>

        {/* Tabs */}
        <div className="mt-3 flex items-center gap-5 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={clsx('py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5',
                tab === t.key ? 'text-primary-600 border-primary-600' : 'text-gray-500 border-transparent hover:text-gray-700')}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === 'detail' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">基本信息</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetaItem label="岗位名称" value={position.name} />
                <MetaItem label="岗位编号" value={position.code} />
                <MetaItem label="所属部门" value={position.department} />
                <MetaItem label="招聘负责人" value={position.owner_name} />
                <MetaItem label="岗位类型" value={HC_EMPLOYMENT_LABELS[position.employment_type as keyof typeof HC_EMPLOYMENT_LABELS]} />
                <MetaItem label="工作地点" value={position.location} />
                <MetaItem label="薪资范围" value={position.salary_range} />
                <MetaItem label="优先级" value={<PriorityBadge value={position.priority} />} />
                <MetaItem label="期望到岗时间" value={fmtDate(position.expected_onboard_date)} />
                <MetaItem label="招聘人数（意图）" value={position.headcount_target ?? 1} />
                <MetaItem label="发布时间" value={fmtDate(position.published_at)} />
                <MetaItem label="招聘状态" value={POSITION_STATUS_LABELS[position.status]} />
              </div>
              {position.background && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-[11px] text-gray-400 mb-1">招聘背景</div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{position.background}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">招聘进展</h3>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <div>
                    <div className="text-xl font-bold text-gray-800">{candidates.length}</div>
                    <div className="text-xs text-gray-400 mt-0.5">候选人总数</div>
                  </div>
                  {PROGRESS_GROUPS.map(g => (
                    <div key={g.label}>
                      <div className={clsx('text-xl font-bold', g.color)}>{countStages(g.stages)}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{g.label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-xl font-bold text-gray-800">{position.bound_count ?? 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">绑定编制</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-primary-600">{position.used_count ?? 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">已占用编制</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-600">{position.offer_count ?? 0}</div>
                    <div className="text-xs text-gray-400 mt-0.5">进行中 Offer</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">快速操作</h3>
                <div className="space-y-2">
                  <button onClick={() => onViewCandidates(positionId)}
                    className="w-full h-9 text-xs text-left px-3 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-2">
                    <Users size={13} className="text-gray-400" />查看候选人（{candidates.length}）
                  </button>
                  <button onClick={() => setTab('interviews')}
                    className="w-full h-9 text-xs text-left px-3 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-2">
                    <CalendarDays size={13} className="text-gray-400" />查看面试安排
                  </button>
                  <button onClick={() => setTab('binding')}
                    className="w-full h-9 text-xs text-left px-3 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-2">
                    <Link2 size={13} className="text-gray-400" />管理编制绑定（{position.bound_count ?? 0}）
                  </button>
                  <button onClick={() => setTab('jd')}
                    className="w-full h-9 text-xs text-left px-3 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-2">
                    <FileText size={13} className="text-gray-400" />查看 JD
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-3">
                  发起 Offer 请在候选人详情页操作，Offer 会自动锁定该岗位绑定的空闲编制。
                </p>
              </div>
            </div>
          </div>
        )}

        {tab === 'binding' && (
          <PositionBindingTab
            position={position}
            currentUser={currentUser}
            onChanged={() => { reload(); onUpdate(); }}
            onOpenSlot={onOpenSlot}
          />
        )}

        {tab === 'screening' && (
          <PositionScreeningTab
            positionId={positionId}
            positionName={position.name}
            isHr={isHr}
          />
        )}

        {tab === 'jd' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl">
            <h2 className="text-base font-bold text-gray-800">{position.name}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {[position.department, position.location, position.salary_range].filter(Boolean).join(' · ') || '—'}
            </p>
            <div className="mt-5 space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1.5">职位描述</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{position.description || '暂无'}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1.5">任职要求</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{position.requirements || '暂无'}</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'candidates' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">候选人 · {candidates.length} 人</h3>
              <button onClick={() => onViewCandidates(positionId)} className="text-xs text-primary-600 hover:text-primary-700">
                在候选人模块中打开
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50">
                  <th className="text-left font-medium px-5 py-2.5">姓名</th>
                  <th className="text-left font-medium px-3 py-2.5">当前公司/职位</th>
                  <th className="text-left font-medium px-3 py-2.5">阶段</th>
                  <th className="text-left font-medium px-3 py-2.5">来源</th>
                  <th className="text-left font-medium px-3 py-2.5">更新时间</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-400">暂无候选人</td></tr>
                ) : candidates.map(c => (
                  <tr key={c.id} onClick={() => onOpenCandidate(c.id)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={c.name} size={22} />
                        <span className="text-xs font-medium text-gray-800">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 truncate max-w-[220px]">
                      {[c.latest_company, c.latest_title].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">{STAGE_LABELS[c.stage] || c.stage}</td>
                    <td className="px-3 py-3 text-xs text-gray-500">{c.source || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-500">{fmtDate(c.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'interviews' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">面试安排</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">展示该岗位候选人近 30 天内外的面试记录</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50">
                  <th className="text-left font-medium px-5 py-2.5">时间</th>
                  <th className="text-left font-medium px-3 py-2.5">候选人</th>
                  <th className="text-left font-medium px-3 py-2.5">轮次</th>
                  <th className="text-left font-medium px-3 py-2.5">面试官</th>
                  <th className="text-left font-medium px-3 py-2.5">状态</th>
                </tr>
              </thead>
              <tbody>
                {interviews.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-400">暂无面试安排</td></tr>
                ) : interviews.map(iv => (
                  <tr key={iv.id} onClick={() => onOpenCandidate(iv.candidate_id)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="px-5 py-3 text-xs text-gray-700">{fmtDateTime(iv.scheduled_time)}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{iv.candidate_name || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-600">{iv.round || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-600">{(iv.interviewer_names || []).join('、') || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-500">
                      {iv.status === 'scheduled' ? '待面试' : iv.status === 'completed' ? '已完成' : '已取消'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'analytics' && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 max-w-3xl">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">阶段分布 · 共 {candidates.length} 人</h3>
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-400">暂无候选人数据</p>
            ) : (
              <div className="space-y-2">
                {FUNNEL_STAGES.map(s => {
                  const count = stageCounts[s] || 0;
                  const pct = Math.round((count / candidates.length) * 100);
                  return (
                    <div key={s} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-20 shrink-0">{STAGE_LABELS[s]}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full transition-all"
                          style={{ width: count ? `${Math.max(pct, 3)}%` : '0%' }} />
                      </div>
                      <span className="text-xs text-gray-600 w-20 text-right">{count} 人 ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 暂停/关闭二次确认 */}
      {confirmIntent && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !acting && setConfirmIntent(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle size={17} className="text-gray-500" />
              {confirmIntent === 'pause' ? '确认暂停招聘？' : '确认关闭岗位？'}
            </h3>
            {preview === null ? (
              <p className="mt-3 text-sm text-gray-400">正在计算将释放的编制…</p>
            ) : (
              <div className="mt-3 space-y-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">将释放的编制（回到空闲，可被其他岗位绑定）</div>
                  {preview.will_release.length === 0 ? (
                    <p className="text-xs text-gray-400">无</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {preview.will_release.map(s => (
                        <span key={s.code} className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-xs">{s.code}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">保留不释放的编制</div>
                  {preview.will_keep.length === 0 ? (
                    <p className="text-xs text-gray-400">无</p>
                  ) : (
                    <ul className="space-y-1">
                      {preview.will_keep.map(s => (
                        <li key={s.code} className="text-xs text-gray-600">
                          <span className="font-medium">{s.code}</span>
                          <span className="text-gray-400"> · {s.status_label}{s.reason ? ` · ${s.reason}` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmIntent(null)} disabled={acting}
                className="h-9 px-4 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">取消</button>
              <button onClick={runTransition} disabled={acting || preview === null}
                className="h-9 px-4 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-lg disabled:opacity-50">
                {acting ? '处理中…' : '确认'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <CreatePositionDrawer
          currentUser={currentUser}
          position={position}
          onClose={() => setShowEdit(false)}
          onSaved={(_saved, goBinding) => {
            setShowEdit(false);
            reload();
            onUpdate();
            toast.success('岗位已更新');
            if (goBinding) setTab('binding');
          }}
        />
      )}
    </div>
  );
}
